/**
 * The single content registry for every `InfoBalloon` in the product (roadmap #12) — one typed map,
 * keyed by a stable section id, so a screen never inlines its own help copy and two screens never
 * drift on how the same concept is described. Copy is grounded in `docs/fsd.md` (FR-IDs) and
 * `docs/data-dictionary.md`, written from the user's side of the screen — plain language, never a
 * raw field/table name — and each entry names the one genuine quirk a user would actually trip on,
 * never an invented behaviour.
 *
 * Anchors this copy is grounded in (do not contradict these — fix the copy instead):
 *  - Case lifecycle tracker: `screens/cases/lifecycle.ts` (monotonic-stepper doc comment) — the
 *    tracker never renders a "skip-ahead" state even if an out-of-order data state exists.
 *  - Program filings / cross-case fields: CLAUDE.md §4 IA (Account · Legal entity · Case · Fiscal
 *    year on every cross-case list).
 *  - Illustrative statutory figures: `docs/raid-log.md` RAID A-4 (Georgia's rate/uplift/threshold
 *    Parameters are authored as labeled ASSUMPTION placeholders pending confirmed statutory
 *    guidance — proving engine mechanics, not statutory accuracy).
 *  - Net proceeds = sale price (already net of discount): `screens/cases/lifecycle.ts`
 *    `LifecycleTransferInput` doc comment + `OverviewPanel`'s own `transferredTotal` convention.
 *  - Eligibility 3-valued aggregation / unresolved gate blocks "eligible": FR-M8.2/FR-M8.3
 *    (`docs/fsd.md` M8).
 *  - Qualified spend guarded adjustment (mandatory comment): FR-M13.2 mirrors FR-M12.5/12.6
 *    (`docs/fsd.md` M12/M13) — CLAUDE.md §2 invariant 1 (guarded writes only via AdjustmentService).
 *  - Finalized computation pins rule/parameter versions, never recomputes: FR-M11.3
 *    (`docs/fsd.md` M11) — CLAUDE.md §2 invariant 3.
 *  - Authority interactions read-only lifecycle: FR-M17 (`docs/fsd.md` M17); `AuthorityPanel.tsx`
 *    doc comment.
 *  - Discount rate vs. discount amount: `docs/data-dictionary.md` §12.5 (`transfer_txn.discount` is
 *    a rate, not a currency amount) + `MonetizationPanel.tsx`'s own `formatDiscountRate` comment.
 *  - Dossier draft/final immutability: FR-M18.2 (`docs/fsd.md` M18) + `docs/data-dictionary.md`
 *    "Dossier" (`dossier_final_immutable` trigger).
 *  - Team history retained, never deleted: `docs/design/IB-51-case-team-design.md` AC5.
 *  - Timeline append-only: FR-M5.3 (`docs/fsd.md` M5) — CLAUDE.md §2 invariant 5/§9 event triggers.
 *  - Evidence readiness score / finalization gate: FR-M14.4/FR-M14.5 (`docs/fsd.md` M14).
 */

export interface HelpContentEntry {
  /** Short heading shown at the top of the balloon — usually the section's own name. */
  title: string
  /** 1–3 plain-language sentences: what the section is, plus its one genuine quirk. */
  body: string
}

export type HelpContentId =
  | 'overview.lifecycle'
  | 'overview.summary'
  | 'overview.filings'
  | 'overview.netProceeds'
  | 'tab.eligibility'
  | 'tab.spend'
  | 'tab.credit'
  | 'tab.authority'
  | 'tab.monetization'
  | 'tab.dossier'
  | 'tab.team'
  | 'tab.timeline'
  | 'tab.evidence'
  | 'tab.tasks'
  | 'list.cases'
  | 'list.clients'
  | 'list.insights'

export const HELP_CONTENT: Record<HelpContentId, HelpContentEntry> = {
  'overview.lifecycle': {
    title: 'Case lifecycle',
    body:
      "Six milestones from assessment to filed outcome, derived from this case's own data. It only moves forward — a step already reached never reverts, even if underlying figures later change.",
  },
  'overview.summary': {
    title: 'Case summary',
    body:
      'The Account, Legal entity, Production (film cases) and Fiscal year this case belongs to — the same four fields every cross-case list in IncentiWise carries, so you can always tell clients and years apart.',
  },
  'overview.filings': {
    title: 'Program filings',
    body:
      'Every jurisdiction/program this case has applied under, with its own lifecycle state and outcome. Note: a newly authored jurisdiction (e.g. Georgia) may still show illustrative placeholder rates pending confirmed statutory guidance.',
  },
  'overview.netProceeds': {
    title: 'Net proceeds',
    body:
      'The sale price the buyer actually paid for the transferred credit — already net of the negotiated discount rate, not the gross certified amount. Shown only once a transfer has been recorded.',
  },
  'tab.eligibility': {
    title: 'Eligibility',
    body:
      'Pass/fail/needs-review verdicts for each subject on this filing, with the reasoning trace behind them. Quirk: one unresolved judgment gate is enough to keep the whole filing from showing as eligible.',
  },
  'tab.spend': {
    title: 'Qualified spend',
    body:
      "The qualified-spend lines behind this case's credit, each with its qualifying % and adjustment history. Read-only here — edits happen only on the production's guarded spend grid, each with a mandatory comment.",
  },
  'tab.credit': {
    title: 'Credit',
    body:
      'The certified credit for each filing and the finalized computation run behind it. Finalizing a run pins its exact rule and parameter versions — a filed figure never silently recomputes later.',
  },
  'tab.authority': {
    title: 'Authority',
    body:
      'Every interaction with a filing authority — submissions, acknowledgements, decisions — and its status history. Read-only: starting or advancing an interaction happens earlier in the filing flow.',
  },
  'tab.monetization': {
    title: 'Monetization',
    body:
      'How a certified credit was sold: buyer, broker, discount rate and the resulting net proceeds. The discount rate shown is a percentage, not a dollar amount — the dollar discount is its own column.',
  },
  'tab.dossier': {
    title: 'Dossier',
    body:
      "The client-deliverable package assembled from this case's own data. A draft regenerates as inputs change; once Finalized a version is locked and hashed — a new version is cut instead of edited.",
  },
  'tab.team': {
    title: 'Team',
    body:
      'Who is assigned to this case and in what role, including people since reassigned or removed — history is kept, never deleted, so you can always see who worked this case and when.',
  },
  'tab.timeline': {
    title: 'Timeline',
    body:
      'An append-only, chronological feed of everything that happened on this case — adjustments, filings, evidence, scope changes. Nothing here can be edited or deleted, only added to.',
  },
  'tab.evidence': {
    title: 'Evidence',
    body:
      "Supporting documents and records linked to this case's filings, with a readiness score for coverage and gaps. A filing can be blocked from finalizing until critical gaps are resolved or waived.",
  },
  'tab.tasks': {
    title: 'Tasks',
    body:
      'Work items tied to this case — reviews, follow-ups, approvals. The same tasks also appear in My Work, filtered here to just this one case.',
  },
  'list.cases': {
    title: 'Cases',
    body:
      'Every R&D and Film & Media case across your clients in one list — use the toggle above to filter by program type. Account, Legal entity, Case and Fiscal year are always shown, never a bare ID.',
  },
  'list.clients': {
    title: 'Accounts',
    body:
      "Every client account your firm manages. An account can hold multiple legal entities, and each legal entity can carry its own cases — drill in to see a client's full structure.",
  },
  'list.insights': {
    title: 'Dashboards',
    body:
      "Portfolio-level charts built from your firm's own case data — credits certified by jurisdiction, pipeline status and more. Filter any tile to narrow it without leaving this screen.",
  },
}
